import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Keyboard,
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import Image from "@components/common/Image";
import { useSelector } from "react-redux";
import Text from "@components/AppText";
import ProductSkeleton from "@components/CMS/Skeleton/ProductSkeleton";
import VerticalProduct from "@components/ProductDisplayUnit/VerticalProduct";
import colors from "@constants/color";
import t from "@constants/localization";
import RequestProduct from "@components/RequestProduct";
import useCMSEvents from "@hooks/useCMSEvents";
import TrendingSearch from "../Components/TrendingSearch";
import NoProducts from "../../../assets/svg/PlacesNoResults.webp";

const PATH = "screens/Search/View/index";

function SearchView(props) {
  const {
    searchProducts = [],
    searchNoProductFoundMessage,
    query,
    loading,
    navigateTo,
    trendingData = [],
    handleTrendingClicked = () => {},
    children,
  } = props;
  const trackEvent = useCMSEvents();

  const [isKeyBoardOpen, setIsKeyBoardOpen] = useState(false);

  const { items } = useSelector((state) => state.cart) || [];

  const _keyboardDidShow = function () {
    setIsKeyBoardOpen(true);
  };
  const _keyboardDidHide = function () {
    setIsKeyBoardOpen(false);
  };

  useEffect(() => {
    Keyboard.addListener("keyboardDidShow", _keyboardDidShow);

    Keyboard.addListener("keyboardDidHide", _keyboardDidHide);

    return () => {
      Keyboard.removeListener("keyboardDidShow", _keyboardDidShow);

      Keyboard.removeListener("keyboardDidHide", _keyboardDidHide);
    };
  }, []);

  const renderCategoriesWithProductTypes = () => {
    return trendingData ? (
      <TrendingSearch
        data={trendingData}
        handleTrendingClicked={handleTrendingClicked}
      />
    ) : (
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          marginVertical: 12,
        }}
      >
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  };

  const emptyComponent = () => {
    return (
      <KeyboardAvoidingView behavior="padding" enabled>
        <ScrollView scrollEnabled>
          <View style={styles.searchContainer}>
            {renderCategoriesWithProductTypes()}
          </View>
          {Array.isArray(trendingData) && trendingData.length ? (
            <RequestProduct navigateTo={navigateTo} fromScreen="Search" />
          ) : (
            <></>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    );
  };

  const noResults = () => {
    return (
      <>
        <View
          style={{
            justifyContent: "center",
            alignItems: "center",
            marginVertical: 36,
            marginHorizontal: 24,
          }}
        >
          <Image
            style={{
              width: 120,
              height: 120,
              marginBottom: 24,
            }}
            source={NoProducts}
          />
          <Text fontType="head3">
            {searchNoProductFoundMessage ||
              t(`${PATH}/noMatchesFound`, { query })}
          </Text>
          <Text
            style={{
              color: colors.silver,
            }}
            fontType="head4"
          >
            {t(`${PATH}/tryAgain`)}
          </Text>
        </View>
      </>
    );
  };

  return (
    <View>
      {loading ? (
        <View
          style={{
            flex: 1,
            flexBasis: "89%",
          }}
        >
          <ProductSkeleton />
          <ProductSkeleton />
          <ProductSkeleton />
          <ProductSkeleton />
          <ProductSkeleton />
          <ProductSkeleton />
          <ProductSkeleton />
        </View>
      ) : searchProducts.length === 0 && query.length > 2 ? (
        noResults()
      ) : (
        <View
          style={{
            flex: 1,
            flexBasis: items && items.length > 0 ? "82%" : "89%",
          }}
        >
          {query ? children : null}
          <FlatList
            data={query ? searchProducts : []}
            keyExtractor={(item) => item.sku_id}
            contentContainerStyle={{
              paddingBottom: 60,
            }}
            onScroll={() => {
              if (isKeyBoardOpen) {
                Keyboard.dismiss();
              }
            }}
            renderItem={({ item, index }) => {
              return (
                <VerticalProduct
                  data={item}
                  routeName="Search"
                  liteViewCart={true}
                  index={index}
                  trackEvent={trackEvent}
                  searchKeyword={query ?? null}
                  keywordHeader={undefined}
                />
              );
            }}
            ListEmptyComponent={emptyComponent()}
            showsVerticalScrollIndicator={false}
            initialNumToRender={8}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  searchContainer: {
    paddingBottom: 20,
    paddingTop: 4,
    width: "100%",
    backgroundColor: colors.searchBg,
    flex: 4,
  },
});

export default SearchView;
